/**
 * Recording the driver's view (ADR-040).
 *
 * The simulator's job is to produce driving data. The episode JSON says what
 * the robot did; this says what the driver could SEE while deciding it, which
 * is the half a video model needs — a telemetry row is not something you can
 * turn into footage, and footage without the matching telemetry is not data.
 *
 * Three rules it exists to keep:
 *
 * 1. **Off unless asked.** Recording is opt-in and per-session. Running the
 *    test suite, a scripted sweep, or ten quick attempts must not leave a pile
 *    of video behind, and must not cost the frames that encoding takes.
 * 2. **It never breaks the run.** Every failure path here — no MediaRecorder,
 *    no codec, a stream that dies mid-run — is caught and reported, and the
 *    attempt carries on. Losing the video of a run is a much smaller cost than
 *    losing the run.
 * 3. **The names match.** The video and the episode JSON are saved under the
 *    same base name, because the whole point is to pair them up later.
 *
 * Implementation is `canvas.captureStream()` into a `MediaRecorder` — no
 * dependency, no server round trip, and the file lands in the player's
 * Downloads folder where they can pick it up. It records whatever camera is
 * live, so a first-person run and a chase-camera run are separate takes; the
 * view is written into the file name so they can be told apart afterwards.
 */

/** Frames per second asked of the canvas stream. */
const CAPTURE_FPS = 30;

/**
 * Container/codec preference.
 *
 * VP9 first for size, VP8 next because it is on every Chromium that has ever
 * shipped, then whatever the browser calls plain WebM, and MP4 last for
 * Safari, which only learned WebM recording recently.
 */
const MIME_CANDIDATES = [
  "video/webm;codecs=vp9",
  "video/webm;codecs=vp8",
  "video/webm",
  "video/mp4",
];

export interface CaptureResult {
  blob: Blob;
  mime: string;
  /** Wall-clock length of the take (ms). */
  ms: number;
}

/** The extension matching a recorder mime type. */
export function extensionFor(mime: string): string {
  return mime.startsWith("video/mp4") ? "mp4" : "webm";
}

/** The first codec this browser will actually record, or null. */
export function supportedMime(): string | null {
  const MR = (globalThis as { MediaRecorder?: typeof MediaRecorder }).MediaRecorder;
  if (!MR || typeof MR.isTypeSupported !== "function") return null;
  return MIME_CANDIDATES.find((m) => MR.isTypeSupported(m)) ?? null;
}

/** Whether this browser can record a canvas at all. */
export function captureSupported(canvas?: HTMLCanvasElement): boolean {
  if (supportedMime() === null) return false;
  if (canvas && typeof canvas.captureStream !== "function") return false;
  return true;
}

/**
 * A single take. One instance per run; `stop()` resolves once the recorder has
 * handed over its last chunk.
 */
export class ViewCapture {
  private recorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private startedAt = 0;
  private mime = "";

  constructor(private readonly canvas: HTMLCanvasElement) {}

  get recording(): boolean {
    return this.recorder !== null && this.recorder.state === "recording";
  }

  /** Begin. Returns the reason it could not, or null on success. */
  start(): string | null {
    if (this.recorder) return "already recording";
    const mime = supportedMime();
    if (mime === null) return "this browser cannot record video";
    if (typeof this.canvas.captureStream !== "function") return "this browser cannot capture the canvas";
    try {
      this.stream = this.canvas.captureStream(CAPTURE_FPS);
      const rec = new MediaRecorder(this.stream, { mimeType: mime });
      this.chunks = [];
      rec.ondataavailable = (e: BlobEvent) => {
        if (e.data && e.data.size > 0) this.chunks.push(e.data);
      };
      // One second per chunk rather than one blob at the end: a tab that is
      // closed mid-run then still has most of its take, and memory does not
      // grow as one ever-larger buffer.
      rec.start(1000);
      this.recorder = rec;
      this.mime = mime;
      this.startedAt = performance.now();
      return null;
    } catch (e) {
      this.release();
      return String(e);
    }
  }

  /** Finish and hand back the file. Never throws; null if there is nothing. */
  async stop(): Promise<CaptureResult | null> {
    const rec = this.recorder;
    if (!rec) return null;
    const ms = performance.now() - this.startedAt;
    try {
      if (rec.state !== "inactive") {
        await new Promise<void>((resolve) => {
          rec.onstop = () => resolve();
          // A recorder that never fires onstop (it happens on some mobile
          // builds when the tab is backgrounded) must not hang the end panel.
          setTimeout(resolve, 2000);
          rec.stop();
        });
      }
    } catch {
      // Fall through: whatever chunks arrived are still worth keeping.
    }
    const chunks = this.chunks;
    const mime = this.mime;
    this.release();
    if (chunks.length === 0) return null;
    return { blob: new Blob(chunks, { type: mime }), mime, ms };
  }

  /** Throw the take away without building a file. */
  cancel(): void {
    try {
      if (this.recorder && this.recorder.state !== "inactive") this.recorder.stop();
    } catch {
      // Nothing to do; releasing the tracks below is what matters.
    }
    this.release();
  }

  private release(): void {
    for (const t of this.stream?.getTracks() ?? []) t.stop();
    this.recorder = null;
    this.stream = null;
    this.chunks = [];
  }
}

/**
 * A file name safe on every platform, built from what the player typed.
 *
 * Anything that is not a letter, digit, dash or underscore becomes a dash,
 * because this string goes straight into a download and a name with a slash
 * or a colon in it either fails or lands somewhere surprising.
 *
 * Accents are STRIPPED rather than replaced. The people naming these runs
 * write Vietnamese, and turning every tone mark into a separator gave
 * "Đường Láng" → "u-o-ng-La-ng": a name nobody can match back to a run.
 * Decomposing and dropping the combining marks gives "Duong-Lang". Đ and đ
 * need saying explicitly — Unicode treats them as letters in their own right,
 * so NFKD leaves them whole and the class below would delete them.
 */
export function safeName(raw: string, fallback = "streetsense"): string {
  const cleaned = raw
    .replace(/Đ/g, "D")
    .replace(/đ/g, "d")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60);
  return cleaned.length > 0 ? cleaned : fallback;
}

/** Hand a recorded take to the browser as a download. */
export function saveCapture(blob: Blob, baseName: string, mime: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${baseName}.${extensionFor(mime)}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked on a timer, not immediately: Safari has not finished reading the
  // blob when click() returns, and revoking straight away saves an empty file.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
